// What a run being drawn would actually lay, square by square.
//
// Pure, and tested, for the same reason `marchView` is: a preview drawn a
// second way is one that can disagree with what it previews. Everything here
// mirrors the two reducer cases exactly — `lay-belt-path` skips a square it
// cannot have and carries on, `lay-belt-line` stops at the first one — so the
// ghost on the board is the set of parts the command is about to create, and
// never a hopeful sketch of the gesture.

import type { Color } from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import { beltPathDirections, canExtendBelt } from "src/engine/rules/industry/belts";
import { cloneIndustry, hasBelt, type IndustryState } from "src/engine/rules/industry/state";
import { payForBelt } from "src/engine/rules/industry/beltParts";
import type { BeltGhost } from "src/api";
import { asSquare, DIRECTION_NAME } from "src/engine/api/names";

/**
 * The parts a run over `squares` would lay, in order.
 *
 * `stopAtBlocked` is the difference between the two ways a run is drawn: a
 * straight line lays its legal prefix and stops, a freehand path skips what it
 * cannot have and keeps going. Getting it the wrong way round would be a
 * preview that promises squares the reducer is about to refuse.
 */
export function beltPreview(
  state: GameState,
  squares: readonly number[],
  owner: Color,
  stopAtBlocked = false
): BeltGhost[] {
  const directions = beltPathDirections(squares);
  if (!directions) return [];

  const out: BeltGhost[] = [];
  // Which entry of `squares` the last ghost came from, so a bend is only drawn
  // where the run genuinely arrives from the part before it. A square skipped
  // in the middle leaves the next one fed by nothing, and an arc drawn there
  // would be a corner the laid lane does not turn.
  let previous = -2;
  // Each part is paid as it is laid (§31 S2), so the preview spends too — on
  // a copy, taken at the first new part — or it promises parts past the money.
  let industry: IndustryState = state.industry;

  for (let i = 0; i < squares.length; i++) {
    const square = squares[i];
    if (canExtendBelt(state.position, industry, square, owner) !== null) {
      if (stopAtBlocked) break;
      continue;
    }
    out.push({
      square: asSquare(square),
      direction: DIRECTION_NAME[directions[i]],
      ...(previous === i - 1 ? { inbound: DIRECTION_NAME[directions[i - 1]] } : {}),
      // Not "is there a belt" but "is there one this lay would only turn" —
      // `canExtendBelt` has already refused anybody else's, so anything still
      // standing here is the player's own.
      repoint: hasBelt(state.industry, square)
    });
    if (!hasBelt(state.industry, square)) {
      if (industry === state.industry) industry = cloneIndustry(state.industry);
      payForBelt(state.position, industry, square, owner);
    }
    previous = i;
  }

  return out;
}

/** The squares a preview covers, for the tile wash under it. */
export const previewSquares = (ghosts: readonly BeltGhost[]): number[] =>
  ghosts.map((ghost) => ghost.square);
